import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useExperienceDefinition from '@/src/ExperienceBuilder/hooks/useExperienceDefinition';
import useExperienceVersions from '@/src/ExperienceBuilder/hooks/useExperienceVersions';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import { SchemaValidator, ValidationError } from '@/src/ExperienceBuilder/validation/schemaValidator';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../../chrome/EventPage';
import { ConfirmDialog, Crumb, Field, SectionCard, TextInput, Textarea } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { Segmented, TCard, TEmpty, TFoot, TSkeleton } from '../../components/table';
import '../../pages-event.css';
import './capture.css';

/*
 * Capture — the booth experience for one event. Every write changes what
 * live booths load and is versioned server-side. The server does no
 * validation, so uploads and edits gate through SchemaValidator first.
 */

type SourceKey = 'upload' | 'template' | 'activation';

const SOURCES: Array<{ value: SourceKey; label: string }> = [
    { value: 'upload', label: 'Upload JSON' },
    { value: 'template', label: 'Template' },
    { value: 'activation', label: 'Another event' },
];

const SOURCE_HELP: Record<SourceKey, string> = {
    upload: 'A definition file exported from the builder or another tool.',
    template: 'Start from a template on your license, with its custom values.',
    activation: 'Copy an existing event’s experience.',
};

const LIVE_NOTE = 'Booths pick this up the next time they load the experience.';
const MAX_ERRORS_SHOWN = 5;
const VERSIONS_PAGE = 8;
const IMPORT_LIMIT = 30;
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

function validationErrors(parsed: any): ValidationError[] {
    const result = SchemaValidator.getInstance().validateDefinition(parsed);
    return result.isValid ? [] : result.errors.filter((e) => e.severity === 'error');
}

interface Confirm {
    title: string;
    body: string;
    actionLabel: string;
    danger?: boolean;
    run: () => Promise<void>;
}

function Issues({ issues }: { issues: ValidationError[] }) {
    if (issues.length === 0) return null;
    return (
        <div className="cap-issues" role="alert">
            <div className="h">
                {issues.length} validation error{issues.length === 1 ? '' : 's'} — nothing was saved
            </div>
            {issues.slice(0, MAX_ERRORS_SHOWN).map((e, i) => (
                <div className="i" key={i}>
                    <span className="p">{e.path}</span>
                    {e.message}
                </div>
            ))}
            {issues.length > MAX_ERRORS_SHOWN && <div className="i">+{issues.length - MAX_ERRORS_SHOWN} more</div>}
        </div>
    );
}

function CaptureBody() {
    const navigate = useNavigate();
    const { activationId: id } = useEventContext();
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const { getDefinition, addDefinition, saveDefinition, deleteDefinition } = useExperienceDefinition();
    const {
        versions,
        currentVersion,
        refresh: refreshVersions,
        restoreVersion,
        applyTemplate,
        importFromActivation,
    } = useExperienceVersions(id);
    const { getActivations } = useActivations();
    const toast = useToast();

    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [hasExperience, setHasExperience] = useState(false);
    const [stringDefinition, setStringDefinition] = useState('');
    const [busy, setBusy] = useState<string | null>(null);
    const busyRef = useRef(false);
    const [confirm, setConfirm] = useState<Confirm | null>(null);

    const [source, setSource] = useState<SourceKey>('upload');
    const [sourceError, setSourceError] = useState<string | null>(null);
    const [uploadIssues, setUploadIssues] = useState<ValidationError[]>([]);
    const fileRef = useRef<HTMLInputElement>(null);

    const [templates, setTemplates] = useState<any[] | null>(null);
    const [templatesError, setTemplatesError] = useState<string | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
    const [templateValues, setTemplateValues] = useState<Record<string, string>>({});

    const [sourceActivations, setSourceActivations] = useState<any[] | null>(null);
    const [importSearch, setImportSearch] = useState('');

    const [editorText, setEditorText] = useState('');
    const [editorIssues, setEditorIssues] = useState<ValidationError[]>([]);
    const [editorError, setEditorError] = useState<string | null>(null);

    const [versionLimit, setVersionLimit] = useState(VERSIONS_PAGE);

    const load = async () => {
        let def: any = null;
        let failed = false;
        try {
            def = await getDefinition(id);
        } catch (e: any) {
            /* 404 = no experience yet; anything else blocks writes */
            failed = e?.response?.status !== 404;
        }
        const text = def?.stringDefinition ?? (def?.definition ? JSON.stringify(def.definition, null, 2) : '');
        setLoadFailed(failed);
        setHasExperience(!!def);
        setStringDefinition(text);
        setEditorText(text);
        setEditorIssues([]);
        setEditorError(null);
        await refreshVersions();
    };

    const initialLoad = async () => {
        setIsLoading(true);
        try {
            await load();
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!orgId || !id) return;
        let mounted = true;
        (async () => {
            try {
                await load();
            } finally {
                if (mounted) setIsLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, orgId]);

    /* lazy-load each source's data the first time it is shown */
    useEffect(() => {
        if (!orgId) return;
        if (source === 'template' && templates == null) {
            authenticatedApi
                .get(`/${orgId}/experience-templates`)
                .then((r) => setTemplates((Array.isArray(r.data) ? r.data : []).filter((t: any) => t.templateType === 1)))
                .catch((e) => {
                    setTemplates([]);
                    setTemplatesError(
                        e?.response?.status === 404 ? 'No templates available on your license.' : 'Couldn’t load templates.',
                    );
                });
        }
        if (source === 'activation' && sourceActivations == null) {
            getActivations()
                .then((rows: any[]) => setSourceActivations((rows ?? []).filter((a) => a.id !== id && !a.isArchived)))
                .catch(() => setSourceActivations([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, orgId]);

    /** one write at a time; reloads on success */
    const runWrite = async (label: string, write: () => Promise<boolean>, done: string, failed: string, onFail: (m: string) => void) => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(label);
        try {
            const ok = await write();
            if (ok) {
                await load();
                setSelectedTemplate(null);
                setUploadIssues([]);
                setSourceError(null);
                toast.show(done);
            } else {
                onFail(failed);
            }
        } finally {
            busyRef.current = false;
            setBusy(null);
        }
    };

    const ask = (c: Confirm) => {
        if (busyRef.current) return;
        setConfirm(c);
    };

    const pickSource = (key: SourceKey) => {
        setSource(key);
        setSourceError(null);
        setUploadIssues([]);
    };

    /* ---- upload ---- */
    const handleFile = async (file: File) => {
        setSourceError(null);
        setUploadIssues([]);
        let parsed: any;
        try {
            parsed = JSON.parse(await file.text());
        } catch {
            setSourceError(`${file.name} isn’t valid JSON.`);
            return;
        }
        const issues = validationErrors(parsed);
        if (issues.length > 0) {
            setUploadIssues(issues);
            return;
        }
        const body = JSON.stringify(parsed);
        ask({
            title: hasExperience ? 'Replace the experience?' : 'Use this experience?',
            body: `${file.name} passed validation and will become the live experience. ${LIVE_NOTE}`,
            actionLabel: hasExperience ? 'Replace' : 'Save',
            run: () =>
                runWrite(
                    'upload',
                    async () =>
                        (hasExperience
                            ? await saveDefinition(id, body).catch(() => null)
                            : await addDefinition(id, body).catch(() => null)) != null,
                    'Experience saved.',
                    'Save failed — try again.',
                    setSourceError,
                ),
        });
    };

    /* ---- template ---- */
    const pickTemplate = (t: any) => {
        setSelectedTemplate(t);
        setSourceError(null);
        try {
            const parsed = JSON.parse(t.definitionJsonString);
            setTemplateValues(
                Object.fromEntries(Object.entries(parsed?.templateValues ?? {}).map(([k, v]) => [k, String(v ?? '')])),
            );
        } catch {
            setTemplateValues({});
        }
    };

    const askApplyTemplate = () => {
        const t = selectedTemplate;
        if (!t) return;
        const values = { ...templateValues };
        ask({
            title: `Apply “${t.name}”?`,
            body: `This ${hasExperience ? 'replaces the current experience' : 'becomes the live experience'}. ${LIVE_NOTE}`,
            actionLabel: 'Apply template',
            run: () =>
                runWrite(
                    'template',
                    async () => (await applyTemplate(t.id, values)) != null,
                    'Template applied.',
                    'Applying the template failed.',
                    setSourceError,
                ),
        });
    };

    /* ---- import ---- */
    const askImport = (a: any) => {
        ask({
            title: `Import from ${a.name}?`,
            body: `Copies that event’s experience here${hasExperience ? ', replacing the current one' : ''}. ${LIVE_NOTE}`,
            actionLabel: 'Import',
            run: () =>
                runWrite(
                    'activation',
                    async () => (await importFromActivation(a.id)) != null,
                    'Experience imported.',
                    'Import failed — that event may not have an experience.',
                    setSourceError,
                ),
        });
    };

    /* ---- versions ---- */
    const askRestore = (versionNumber: number) => {
        ask({
            title: `Restore version ${versionNumber}?`,
            body: `Version ${versionNumber} becomes the live experience as a new version; history is kept. ${LIVE_NOTE}`,
            actionLabel: 'Restore',
            run: () =>
                runWrite(
                    `restore-${versionNumber}`,
                    async () => (await restoreVersion(versionNumber)) != null,
                    `Restored version ${versionNumber}.`,
                    'Restore failed — try again.',
                    (m) => toast.show(m),
                ),
        });
    };

    /* ---- editor ---- */
    const editorDirty = editorText !== stringDefinition;

    const askSaveEditor = () => {
        if (busyRef.current) return;
        setEditorError(null);
        setEditorIssues([]);
        let parsed: any;
        try {
            parsed = JSON.parse(editorText);
        } catch (e: any) {
            setEditorError(`Not valid JSON — ${e?.message ?? 'parse error'}`);
            return;
        }
        const issues = validationErrors(parsed);
        if (issues.length > 0) {
            setEditorIssues(issues);
            return;
        }
        const body = JSON.stringify(parsed);
        ask({
            title: 'Save the definition?',
            body: `Your edits pass validation and will become the live experience. ${LIVE_NOTE}`,
            actionLabel: 'Save',
            run: () =>
                runWrite(
                    'editor',
                    async () => (await saveDefinition(id, body).catch(() => null)) != null,
                    'Experience saved.',
                    'Save failed — try again.',
                    setEditorError,
                ),
        });
    };

    const discardEditor = () => {
        setEditorText(stringDefinition);
        setEditorIssues([]);
        setEditorError(null);
    };

    /* ---- export / delete ---- */
    const exportJson = () => {
        const blob = new Blob([stringDefinition], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `experience-${id}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const askDelete = () => {
        ask({
            title: 'Delete the experience?',
            body: 'Booths running clickerbooth will have nothing to load until a new experience is added. Earlier versions stay restorable from the version history.',
            actionLabel: 'Delete experience',
            danger: true,
            run: () =>
                runWrite(
                    'delete',
                    async () => (await deleteDefinition(id).then(() => true).catch(() => false)),
                    'Experience deleted.',
                    'Delete failed — try again.',
                    (m) => toast.show(m),
                ),
        });
    };

    const isBusy = busy != null;
    const filteredSources = (sourceActivations ?? []).filter((a) =>
        `${a.name}`.toLowerCase().includes(importSearch.trim().toLowerCase()),
    );

    const sub = isLoading
        ? 'Loading…'
        : loadFailed
          ? 'Couldn’t load the experience'
          : hasExperience
            ? currentVersion != null
                ? `Version ${currentVersion} is live`
                : 'Experience configured'
            : 'No experience yet';

    return (
        <div className="ev-page">
            <Crumb label="Overview" onClick={() => navigate(`/activations/${id}`)} />
            <PageHeadActions
                title="Capture"
                sub={sub}
                actions={
                    <>
                        <button type="button" className="tb-btn" onClick={() => navigate(`/activations/${id}/processing`)}>
                            Processing flows
                        </button>
                        {!isLoading && !loadFailed && hasExperience && (
                            <>
                                <button type="button" className="tb-btn" onClick={exportJson}>
                                    Export JSON
                                </button>
                                <button type="button" className="tb-btn danger-soft" disabled={isBusy} onClick={askDelete}>
                                    {busy === 'delete' ? 'Deleting…' : 'Delete'}
                                </button>
                            </>
                        )}
                    </>
                }
            />

            {isLoading ? (
                <TCard>
                    <TSkeleton rows={3} />
                </TCard>
            ) : loadFailed ? (
                <TCard>
                    <TEmpty message="Couldn’t load this event’s experience." actionLabel="Retry" onAction={initialLoad} />
                </TCard>
            ) : (
                <div className="stack">
                    <SectionCard title="Status">
                        <div className="cap-status">
                            <span className={hasExperience ? 'cap-dot on' : 'cap-dot'} aria-hidden="true" />
                            {hasExperience ? 'Experience configured' : 'No experience yet'}
                        </div>
                        <p className="cap-status-sub">
                            {hasExperience
                                ? `${currentVersion != null ? `Version ${currentVersion} is live. ` : ''}Devices running clickerbooth load this experience.`
                                : 'ROMR shoots don’t need one — the app captures on its own. Events using clickerbooth need an experience before devices can capture.'}
                        </p>
                    </SectionCard>

                    <SectionCard
                        title={hasExperience ? 'Replace experience' : 'Start from'}
                        note={hasExperience ? 'Every change is versioned — restore below' : undefined}
                    >
                        <Segmented<SourceKey> label="Experience source" value={source} onChange={pickSource} options={SOURCES} />
                        <p className="field-h cap-panel">{SOURCE_HELP[source]}</p>

                        {source === 'upload' && (
                            <div className="cap-panel">
                                <button
                                    type="button"
                                    className="tb-btn"
                                    disabled={isBusy}
                                    onClick={() => fileRef.current?.click()}
                                >
                                    {busy === 'upload' ? 'Saving…' : 'Choose a .json file'}
                                </button>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="application/json,.json"
                                    aria-label="Experience JSON file"
                                    hidden
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) void handleFile(f);
                                        e.target.value = '';
                                    }}
                                />
                                <Issues issues={uploadIssues} />
                            </div>
                        )}

                        {source === 'template' && (
                            <div className="cap-panel">
                                {templates == null ? (
                                    <TCard>
                                        <TSkeleton rows={2} />
                                    </TCard>
                                ) : templatesError ? (
                                    <p className="quiet-empty">{templatesError}</p>
                                ) : templates.length === 0 ? (
                                    <p className="quiet-empty">No experience templates on your license.</p>
                                ) : (
                                    <TCard>
                                        {templates.map((t) => (
                                            <button
                                                type="button"
                                                key={t.id}
                                                className="trow cap-pick"
                                                aria-pressed={selectedTemplate?.id === t.id}
                                                disabled={isBusy}
                                                onClick={() => pickTemplate(t)}
                                            >
                                                <span className="main">
                                                    <span className="nm">{t.name}</span>
                                                    {t.description && <span className="sub">{t.description}</span>}
                                                </span>
                                                {selectedTemplate?.id === t.id && <span className="ev-st ok">Selected</span>}
                                            </button>
                                        ))}
                                    </TCard>
                                )}

                                {selectedTemplate && (
                                    <>
                                        {Object.keys(templateValues).length > 0 && (
                                            <div className="cap-vars">
                                                {Object.entries(templateValues).map(([k, v]) => (
                                                    <Field key={k} label={k}>
                                                        <span className="cap-color">
                                                            {HEX.test(v) && (
                                                                <input
                                                                    type="color"
                                                                    aria-label={`${k} color`}
                                                                    value={v.length === 4 ? `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}` : v}
                                                                    disabled={isBusy}
                                                                    onChange={(e) =>
                                                                        setTemplateValues((tv) => ({ ...tv, [k]: e.target.value }))
                                                                    }
                                                                />
                                                            )}
                                                            <TextInput
                                                                className="input ev-mono"
                                                                aria-label={k}
                                                                value={v}
                                                                disabled={isBusy}
                                                                onChange={(e) =>
                                                                    setTemplateValues((tv) => ({ ...tv, [k]: e.target.value }))
                                                                }
                                                            />
                                                        </span>
                                                    </Field>
                                                ))}
                                            </div>
                                        )}
                                        <div className="cap-actions">
                                            <button type="button" className="tb-new" disabled={isBusy} onClick={askApplyTemplate}>
                                                {busy === 'template' ? 'Applying…' : 'Apply template'}
                                            </button>
                                            <button
                                                type="button"
                                                className="tb-btn ghost"
                                                disabled={isBusy}
                                                onClick={() => setSelectedTemplate(null)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {source === 'activation' && (
                            <div className="cap-panel">
                                <Field label="Search events">
                                    <TextInput
                                        type="search"
                                        placeholder="Event name…"
                                        value={importSearch}
                                        onChange={(e) => setImportSearch(e.target.value)}
                                    />
                                </Field>
                                <div className="ev-gap">
                                    {sourceActivations == null ? (
                                        <TCard>
                                            <TSkeleton rows={3} />
                                        </TCard>
                                    ) : filteredSources.length === 0 ? (
                                        <p className="quiet-empty">
                                            {sourceActivations.length === 0 ? 'No other events to import from.' : 'No events match.'}
                                        </p>
                                    ) : (
                                        <TCard>
                                            <div className="cap-scroll">
                                                {filteredSources.slice(0, IMPORT_LIMIT).map((a) => (
                                                    <button
                                                        type="button"
                                                        key={a.id}
                                                        className="trow cap-pick"
                                                        disabled={isBusy}
                                                        onClick={() => askImport(a)}
                                                    >
                                                        <span className="main">
                                                            <span className="nm">{a.name}</span>
                                                        </span>
                                                        <span className="ev-st muted">
                                                            {a.date ? moment(a.date).format('MMM D, YYYY') : ''}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        </TCard>
                                    )}
                                </div>
                                <p className="field-h ev-gap">
                                    {busy === 'activation'
                                        ? 'Importing…'
                                        : filteredSources.length > IMPORT_LIMIT
                                          ? `Showing ${IMPORT_LIMIT} of ${filteredSources.length} — search to narrow. `
                                          : ''}
                                    Importing copies the source experience — pick an event that has one.
                                </p>
                            </div>
                        )}

                        {sourceError && (
                            <p className="field-e ev-gap" role="alert">
                                {sourceError}
                            </p>
                        )}
                    </SectionCard>

                    {versions.length > 0 && (
                        <div className="sec">
                            <div className="sec-h">
                                <h2>Version history</h2>
                                <span className="n">{versions.length}</span>
                            </div>
                            <TCard>
                                {versions.slice(0, versionLimit).map((v) => (
                                    <div className="trow ev-row" key={v.id}>
                                        <span className="main">
                                            <span className="nm cap-ver">
                                                v{v.versionNumber}
                                                {v.source && <span className="badge">{v.source}</span>}
                                            </span>
                                            <span className="sub">
                                                {[v.createdBy, moment(v.createdAt).fromNow()].filter(Boolean).join(' · ')}
                                            </span>
                                        </span>
                                        {v.versionNumber === currentVersion ? (
                                            <span className="ev-st ok">Live</span>
                                        ) : (
                                            <button
                                                type="button"
                                                className="tb-btn sm"
                                                disabled={isBusy}
                                                onClick={() => askRestore(v.versionNumber)}
                                            >
                                                {busy === `restore-${v.versionNumber}` ? 'Restoring…' : 'Restore'}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </TCard>
                            {versions.length > versionLimit && (
                                <TFoot>
                                    <div className="ev-foot">
                                        <span>
                                            {versionLimit} of {versions.length}
                                        </span>
                                        <button
                                            type="button"
                                            className="tb-btn"
                                            onClick={() => setVersionLimit((l) => l + VERSIONS_PAGE)}
                                        >
                                            Show more
                                        </button>
                                    </div>
                                </TFoot>
                            )}
                        </div>
                    )}

                    {hasExperience && (
                        <SectionCard title="Definition" note={editorDirty ? 'unsaved edits' : 'raw JSON'}>
                            <label className="field wide">
                                <span className="field-l">Experience JSON</span>
                                <Textarea
                                    className="input textarea cap-json"
                                    rows={18}
                                    wrap="off"
                                    value={editorText}
                                    disabled={isBusy}
                                    aria-invalid={editorError || editorIssues.length > 0 ? true : undefined}
                                    onChange={(e) => {
                                        setEditorText(e.target.value);
                                        setEditorError(null);
                                        setEditorIssues([]);
                                    }}
                                />
                                <span className="field-h">Validated before saving — invalid JSON is never sent.</span>
                            </label>
                            {editorError && (
                                <p className="field-e ev-gap" role="alert">
                                    {editorError}
                                </p>
                            )}
                            <Issues issues={editorIssues} />
                            {editorDirty && (
                                <div className="cap-actions">
                                    <button type="button" className="tb-new" disabled={isBusy} onClick={askSaveEditor}>
                                        {busy === 'editor' ? 'Saving…' : 'Save changes'}
                                    </button>
                                    <button type="button" className="tb-btn ghost" disabled={isBusy} onClick={discardEditor}>
                                        Discard
                                    </button>
                                </div>
                            )}
                        </SectionCard>
                    )}
                </div>
            )}

            <ConfirmDialog
                open={confirm != null}
                title={confirm?.title ?? ''}
                body={confirm?.body ?? ''}
                actionLabel={confirm?.actionLabel ?? 'Confirm'}
                danger={confirm?.danger ?? false}
                onConfirm={() => {
                    if (confirm) void confirm.run();
                }}
                onClose={() => setConfirm(null)}
            />
            {toast.node}
        </div>
    );
}

export default function CaptureV3() {
    return (
        <EventPage section="capture">
            <CaptureBody />
        </EventPage>
    );
}
